import { GridApiCommon, GridColDef, GridRowId } from "@mui/x-data-grid";
import { TFunction } from "i18next";
import { memo } from "react";
import { TbFriends } from "react-icons/tb";

import ClipBoard from "@/components/ClipBoard";
import { TableHeader } from "@/components/TableHeader";
import MemoizedEditableColumn from "@/components/table/EditableColumn";
import { capitalizeWords } from "@/helpers/strings";
import { CompanionsSchema } from "@/schemas/companions";
import { usePlanActions } from "@/stores/use-plan-table";
import { PassengerTypes } from "@/types/global";
import { IPlanPassenger } from "@/types/tasks.types";

const getCompanionName = (companion: string | { name: string }) => {
    if (!companion) return "";
    if (typeof companion === "string") return companion;
    return companion.name;
};
const MemoizedContent = memo(function ({ companions }: { companions: string[] }) {
    return (
        <div className="flex !p-1 !pl-2  w-full items-center h-full gap-2 ">
            <div className="font-medium flex  flex-wrap  flex-row w-full text-xs text-gray-700 !capitalize ">
                {companions &&
                    companions.map((user, index) => (
                        <ClipBoard key={index} text={capitalizeWords(getCompanionName(user))}>
                            {capitalizeWords(getCompanionName(user))}
                            {index !== companions.length - 1 && ", "}
                        </ClipBoard>
                    ))}
            </div>
        </div>
    );
});

const MemoizedCell = memo(function ({
    value,
    apiRef,
    field,
    id,
    name,
    cancelled,
    passengerType,
    fullCompanions,
    editable,
}: {
    id: GridRowId;
    field: string;
    apiRef: GridApiCommon;
    value: string[];
    name: string;
    cancelled: boolean;
    passengerType: PassengerTypes;
    fullCompanions: CompanionsSchema;
    editable: boolean;
}) {
    const { updateShowUpdateCompanionsDialog } = usePlanActions();
    const handleEdit = () => {
        updateShowUpdateCompanionsDialog({
            id: id as string,
            name,
            cancelled,
            passengerType,
            fullCompanions,
        });
    };

    return (
        <MemoizedEditableColumn
            renderHistoryComp={(val) => <MemoizedContent companions={val as string[]} />}
            api={apiRef}
            field={field}
            id={id}
            editable={editable}
            handleEdit={handleEdit}
        >
            <MemoizedContent companions={value} />
        </MemoizedEditableColumn>
    );
});

export const CompanionsColumn = ({
    t,
    editable,
}: {
    t: TFunction;
    editable: boolean;
}): GridColDef<IPlanPassenger<"api">> => {
    return {
        field: "companions",
        headerName: t("companions"),
        minWidth: 180,
        maxWidth: 450,
        flex: 1,
        valueGetter: (_val, row) => row.companions,
        renderHeader: (params) => <TableHeader Component={TbFriends} title={params.colDef.headerName!} />,
        renderCell: ({ api, value, row, field }) => (
            <MemoizedCell
                name={row.name}
                apiRef={api}
                field={field}
                passengerType={row.passengerType}
                cancelled={row.cancelled}
                id={row.id}
                value={value}
                fullCompanions={row.fullCompanions || row.companions}
                editable={editable}
            />
        ),
    };
};
